// FeedbackView.js
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import docImg from "../../../../assets/icons/document.png";

import { GET, POST, newFetch } from "../../../../helpers/fetch";
import Spinner from "../../../SpringLoader/index";
import ToastUtil from "../../../ToastNotification/ToastUtil";

import { Card } from "reactstrap";

const FeedbackView = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const [loading, setLoading] = useState(true);

  // feedback data
  const initialFeedback = location.state?.feedback;
  const [feedback, setFeedback] = useState(initialFeedback || null);

  // messages
  const [messages, setMessages] = useState([]);
  const [curMessage, setCurMessage] = useState("");
  const [messageBox, setMessageBox] = useState(null);
  const messageRef = useRef(null);

  // current user (assumes same localStorage pattern as your other components)
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // helper: fetch feedback detail (adjust API path as necessary)
  const fetchFeedback = async (id) => {
    try {
      setLoading(true);
      // Attempt to call an endpoint for single feedback
      // Adjust this path to match your backend if different.
      const apiPath = `/feedback/${id}`;
      const res = await newFetch(GET, apiPath);
      if (res && res.success) {
        setFeedback(res.data);
      } else {
        // fallback: if API not available, retain initialFeedback
        if (!initialFeedback)
          ToastUtil.error(res?.message || "Unable to fetch feedback");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // fetch messages for the feedback
  const fetchMessages = async (feedbackId) => {
    if (!feedbackId) return;
    try {
      const res = await newFetch(GET, `/feedback-message/${feedbackId}`);
      if (res && res.success) {
        setMessages(res.data);
        // auto scroll to bottom after messages update
        setTimeout(() => {
          if (messageRef.current) {
            messageRef.current.scrollTop = messageRef.current.scrollHeight;
          }
        }, 50);
      } else {
        ToastUtil.error(res?.message || "Failed to fetch messages");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // send message
  const addMessage = async () => {
    if (!curMessage || !curMessage.trim()) return;
    if (!feedback || !feedback.id)
      return ToastUtil.error("Missing feedback id");

    const payload = {
      feedback_id: feedback.id,
      message: curMessage,
      type: user?.role === "admin" ? "admin" : "associate",
      added_by: user?.id,
    };

    try {
      const res = await newFetch(POST, `/feedback-message`, payload);
      if (res && res.success) {
        setCurMessage("");
        // append the created message to UI
        setMessages((prev) => [...prev, res.data]);
        // scroll
        setTimeout(() => {
          if (messageRef.current)
            messageRef.current.scrollTop = messageRef.current.scrollHeight;
        }, 50);
      } else {
        ToastUtil.error(res?.message || "Failed to send message");
      }
    } catch (err) {
      console.error(err);
      ToastUtil.error("Internal server error");
    }
  };

  const onKeyPress = (e) => {
    if (e.key === "Enter") addMessage();
  };

  // initial load: if no initialFeedback but URL param present, fetch
  useEffect(() => {
    const idFromParams = params?.feedbackId || params?.id; // adjust if route uses different param name
    if (!feedback && idFromParams) {
      fetchFeedback(idFromParams);
    } else {
      setLoading(false);
    }
  }, [params, initialFeedback]);

  // whenever feedback is set, fetch messages
  useEffect(() => {
    if (feedback && feedback.id) {
      fetchMessages(feedback.id);
    }
  }, [feedback]);

  if (loading) return <Spinner />;

  if (!feedback) {
    return (
      <div className="container pt-4">{t("No feedback data available")}</div>
    );
  }

  return (
    <div className="container pt-4">
      <h4 className="text-brand">
        <Link to="/communications">
          <i className="fas fa-arrow-left link" style={{ cursor: "pointer" }} />
        </Link>
        &nbsp;&nbsp;{t("Manage Feedback")}
      </h4>

      {/* NEW LAYOUT: Title -> Description -> Documents -> Chatbox */}
      <div className="card mb-4 mt-5">
        <div className="card-body">
          {/* Title */}
          <div className="mb-3">
            <div className="header-thick">{t("Title")}</div>
            <div className="pt-2">{feedback.title || "-"}</div>
          </div>

          {/* Description */}
          <div className="mb-3">
            <div className="header-thick">{t("Description")}</div>
            <div
              className="pt-2"
              dangerouslySetInnerHTML={{ __html: feedback.description || "-" }}
            />
          </div>

          {/* Documents */}
          <div className="mb-3">
            <div className="pt-2">
              {feedback.documents && feedback.documents.length > 0 ? (
                <div className="d-flex flex-wrap gap-3">
                  {feedback.documents.map((doc, idx) => (
                    <div key={idx}>
                      <a
                        href={`${process.env.REACT_APP_SERVER_URL || ""}/${
                          doc.url
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex flex-column align-items-center text-decoration-none"
                      >
                        <img
                          src={docImg}
                          alt="Document Icon"
                          className="me-2"
                          style={{ width: "32px", height: "32px" }}
                        />
                      </a>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Chatbox */}
      <div
        className="d-flex"
        style={{ marginTop: "1rem", marginLeft: "0", marginRight: "0" }}
      >
        <div className="w-100 user-chat mt-0 mt-sm-0 mb-5">
          <Card>
            <PerfectScrollbar
              className="chat-conversation p-3 px-2"
              style={{ height: "420px" }}
              containerRef={(ref) => {
                setMessageBox(ref);
                messageRef.current = ref;
              }}
            >
              <ul
                className="list-unstyled mb-0"
                style={
                  messages.length === 0
                    ? {
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }
                    : {}
                }
              >
                {messages.length === 0 && (
                  <div
                    style={{
                      color: "#999",
                      fontSize: "14px",
                    }}
                  >
                    {t("Send a message to start the conversation")}
                  </div>
                )}
                <br />
                {messages.map((message) => (
                  <li
                    key={"msg_" + message.id}
                    className={message.added_by === user.id ? "right" : ""}
                  >
                    <div className="conversation-list">
                      <div className="conversation-item">
                        <div className="conversation-name">
                          <span className="user-name">
                            {message.sender
                              ? `${message.sender.first_name} ${message.sender.last_name}`
                              : "User"}
                          </span>
                          <span className="time">
                            {moment(message.createdAt).format(
                              "DD/MM/YYYY hh:mm A"
                            )}
                          </span>
                        </div>

                        <div className="ctext-wrap">
                          <div className="ctext-wrap-content">
                            <p className="mb-0">{message.message}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </PerfectScrollbar>

            {/* Input */}
            <div className="p-3 border-top">
              <label className="font-weight-bold">{t("Message")}</label>

              <textarea
                rows={5}
                value={curMessage}
                onKeyPress={onKeyPress}
                onChange={(e) => setCurMessage(e.target.value)}
                className="form-control border bg-light-subtle"
              ></textarea>

              {/* Button below textarea, aligned right on larger screens */}
              <div className="mt-3">
                <button
                  onClick={addMessage}
                  className="btn btn-brand adjust-fs adjust-shadow w-100"
                >
                  {t("Submit")}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeedbackView;
